import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import ChatIcon from '@mui/icons-material/Chat';
import MicIcon from '@mui/icons-material/Mic';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { alpha, useTheme } from '@mui/material';

export type InterrogationInputMode = 'chat' | 'live';

interface InterrogationInputModeToggleProps {
  mode: InterrogationInputMode;
  onModeChange: (mode: InterrogationInputMode) => void;
  isCompleted: boolean;
  isListening: boolean;
  onFinish: () => void;
}

export function InterrogationInputModeToggle({
  mode,
  onModeChange,
  isCompleted,
  isListening,
  onFinish,
}: InterrogationInputModeToggleProps) {
  const theme = useTheme();

  const handleModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    next: InterrogationInputMode | null,
  ) => {
    if (next !== null && !isListening) {
      onModeChange(next);
    }
  };

  return (
    <Box
      sx={{
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        bgcolor: theme.palette.background.paper,
        px: 2,
        pt: 1.5,
        pb: 1,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          maxWidth: '960px',
          mx: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={handleModeChange}
            size="small"
            disabled={isCompleted || isListening}
            aria-label="interrogation input mode"
          >
            <ToggleButton value="chat" aria-label="chat mode">
              <ChatIcon sx={{ fontSize: 18, mr: 0.75 }} />
              Chat
            </ToggleButton>
            <ToggleButton value="live" aria-label="live mode">
              <MicIcon sx={{ fontSize: 18, mr: 0.75 }} />
              Live
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            {mode === 'chat'
              ? 'Type answers in the conversation below.'
              : isListening
                ? 'Recording in progress — transcript updates above.'
                : 'Start recording to capture speech from the meeting.'}
          </Typography>
        </Box>

        {!isCompleted && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<StopCircleIcon />}
            onClick={onFinish}
            sx={{ flexShrink: 0 }}
          >
            Finish session
          </Button>
        )}
      </Box>
    </Box>
  );
}
